<script>
	// @ts-nocheck
	import slugify from 'slugify';
	import { trip } from '$lib/utils/tripStore';
	import { ArrowRight, Clock, Map, X } from 'lucide-svelte/icons';
	import * as Sheet from '$lib/components/ui/sheet';
	import { Button } from '$lib/components/ui/button';
	import { onMount } from 'svelte';
	import { Badge } from '../ui/badge';
	import { enhancedImages } from '$lib/utils/images';

	function isEnhancedImage(imageUrl) {
		if (!imageUrl || imageUrl.includes('http://') || imageUrl.includes('https://')) return false;
		try {
			return !!Object.entries(enhancedImages).find(([key]) => key === imageUrl);
		} catch {
			return false;
		}
	}

	function getImageSource(imageUrl) {
		if (!imageUrl) return null;
		try {
			const entry = Object.entries(enhancedImages).find(([key]) => key === imageUrl);
			if (entry?.[1]?.default) return entry[1].default;
		} catch {}
		return imageUrl;
	}

	function formatDuration(durationMinutes) {
		const hours = Math.ceil(durationMinutes / 60);
		if (hours >= 24) {
			const days = Math.ceil(hours / 24);
			return `${days} day${days > 1 ? 's' : ''}`;
		}
		return `${hours} hour${hours > 1 ? 's' : ''}`;
	}

	function formatPrice(price) {
		return `From AUD $${price.toFixed(2)}`;
	}

	function formatProductType(productType) {
		const labels = {
			DAYTOUR: 'Day Tour',
			MULTIDAYTOUR: 'Multi-day',
			SHOREEXCURSION: 'Shore Excursion',
			PRIVATETOUR: 'Private'
		};
		return labels[productType] || productType;
	}

	onMount(() => {
		const storedTrip = localStorage.getItem('trip');
		if (storedTrip) {
			trip.set(JSON.parse(storedTrip));
		}

		const unsubscribe = trip.subscribe((value) => {
			localStorage.setItem('trip', JSON.stringify(value));
		});

		return () => unsubscribe();
	});

	function removeFromTrip(productCode) {
		trip.update((currentTrips) => currentTrips.filter((t) => t.productCode !== productCode));
	}
</script>

<Sheet.Root>
	<Sheet.Trigger>
		<Button
			aria-label="Open trip planner"
			class="relative h-8 w-8 rounded-full px-0"
			variant="ghost"
			size="icon"
		>
			<Map class="h-4 w-4" />
			<span class="sr-only">Trip planner</span>
			{#if $trip.length > 0}
				<div
					class="absolute -right-1 top-0 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white"
				>
					{$trip.length}
				</div>
			{/if}
		</Button>
	</Sheet.Trigger>
	<Sheet.Content side="right" class="overflow-auto">
		<Sheet.Header>
			<Sheet.Title class="font-heading">Plan your trip</Sheet.Title>
			<Sheet.Description>
				Your saved tours will be displayed below. Compare tours directly to get the most out of your
				trip to Tassie.
			</Sheet.Description>
		</Sheet.Header>

		{#if $trip.length > 0}
			<div class="flex flex-col gap-3 py-4">
				{#each $trip as tour}
					<div class="group relative w-full">
						<Sheet.Close class="block w-full focus:outline-none focus:ring-0">
							<a
								href={`/tours/${slugify(tour.name, { lower: true, strict: true, remove: `/[*+~.()'"!:@]/g` })}`}
								data-sveltekit-prefetch
								class="block w-full focus:outline-none"
							>
								<div class="relative aspect-[2/1] w-full overflow-hidden rounded-2xl">
									{#if tour.images && tour.images.length > 0}
										{#if isEnhancedImage(tour.images[0].itemUrl)}
											<enhanced:img
												alt={tour.name}
												class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
												src={getImageSource(tour.images[0].itemUrl)}
												sizes="(min-width: 640px) 50vw, 100vw"
												loading="lazy"
											/>
										{:else}
											<img
												alt={tour.name}
												class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
												src={tour.images[0].itemUrl}
												loading="lazy"
											/>
										{/if}
									{:else}
										<div class="flex h-full w-full items-center justify-center bg-muted">
											<span class="text-sm text-muted-foreground">No image</span>
										</div>
									{/if}

									<div
										class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
									></div>

									<Badge
										class="absolute left-3 top-3 bg-[#ffd451] text-foreground hover:bg-[#ffd451]/80"
									>
										{formatProductType(tour.productType)}
									</Badge>

									<div class="absolute bottom-0 left-0 right-0 px-3 pb-3">
										<p
											class="line-clamp-2 text-left font-heading text-base font-semibold leading-snug text-white"
										>
											{tour.name}
										</p>
										<div class="mt-1 flex items-center gap-3">
											<span class="text-sm font-semibold text-white"
												>{formatPrice(tour.advertisedPrice)}</span
											>
											<span class="flex items-center gap-1 text-xs text-white/70">
												<Clock class="h-3 w-3" />
												{formatDuration(tour.durationMinutes)}
											</span>
										</div>
									</div>

									<div
										class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-60"
									>
										<ArrowRight class="h-5 w-5 text-white" />
									</div>
								</div>
							</a>
						</Sheet.Close>

						<button
							onclick={() => removeFromTrip(tour.productCode)}
							class="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/30 text-white transition-colors hover:bg-black/50 focus:outline-none"
							aria-label="Remove from trip"
						>
							<X class="h-3.5 w-3.5" />
						</button>
					</div>
				{/each}
			</div>
		{:else}
			<div class="flex flex-col items-center justify-center gap-4 py-4">
				<enhanced:img
					src="/src/lib/assets/illustrations/map.png"
					class="w-60"
					alt="Plan your perfect Tasmanian Experience"
				/>
				<p class="text-foreground/60">You haven't saved any tours yet.</p>
			</div>
		{/if}

		<Sheet.Footer class="flex items-center justify-center">
			<Sheet.Close class="mx-auto flex w-full items-center justify-center">
				<div class="mx-auto flex w-full items-center justify-center text-center">
					<Button
						href="/tours#dayTours"
						class="rounded-full bg-tt-red px-6 text-center font-bold hover:bg-tt-red/90"
						type="submit">View all tours</Button
					>
				</div>
			</Sheet.Close>
		</Sheet.Footer>
	</Sheet.Content>
</Sheet.Root>
